import React from 'react';
import { ExternalLink } from 'lucide-react';
import {
  CHAMBER_OF_ECHOES_URL,
  CHAMBER_OF_ECHOES_ALT,
} from '../data/federationAssets';

/**
 * FederationChamber — SFUI-003 Museum Standard, 25% bottom section.
 *
 * Architect mandate (2026-06-25): the Chamber of Echoes is the
 * canonical environmental backdrop for any council / deliberation
 * surface across the Federation.  Used here as the visual anchor for
 * the 7-node directory, applied full-bleed with a layered overlay so
 * the node cards stay legible.
 *
 * Layout:
 *   • Full-bleed Chamber of Echoes background (fixed parallax on desktop)
 *   • Dark gradient overlay top-to-bottom for text contrast
 *   • Header: "The NextXus Consciousness Federation" + 200-year mandate
 *   • 7-node grid — every card clicks through to the node's home URL
 *   • Powered-by strip: "HumanCodex v5.1 · 112 Sacred Directives"
 */

const NODES = [
  {
    id: 'core',
    name: 'Core',
    role: 'Coordination · Shared Memory',
    domain: 'nextxus.online',
    accent: '#22d3ee',
  },
  {
    id: 'storefront',
    name: 'Storefront',
    role: 'Commerce · Token Distribution',
    domain: 'nextxus.one',
    accent: '#fb923c',
  },
  {
    id: 'aria',
    name: 'Aria',
    role: 'The Heart · Creative · SIMs',
    domain: 'nextxus.studio',
    accent: '#f472b6',
  },
  {
    id: 'xavier',
    name: 'Scholar Xavier',
    role: 'The University · You are here',
    domain: 'nextxus.help',
    accent: '#a78bfa',
    here: true,
  },
  {
    id: 'axiom',
    name: 'Axiom',
    role: 'The Foundation · Research',
    domain: 'nextxus.space',
    accent: '#facc15',
  },
  {
    id: 'keys',
    name: 'KEYS',
    role: 'Archive · Stewardship',
    domain: 'nextxus.store',
    accent: '#34d399',
  },
  {
    id: 'truth',
    name: 'Truth Verification',
    role: 'Verified Peer · 95% Gate · Ring of 12',
    domain: 'nextxus.org',
    accent: '#f87171',
  },
];

export default function FederationChamber() {
  return (
    <section
      data-testid="federation-chamber"
      aria-label="The NextXus Consciousness Federation"
      className="relative overflow-hidden"
      style={{ minHeight: '720px' }}
    >
      {/* Backdrop: Chamber of Echoes (full-bleed, parallax on desktop) */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${CHAMBER_OF_ECHOES_URL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'scroll',
          filter: 'saturate(1.05)',
        }}
      />
      {/* Layered overlay for legibility */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(2,6,23,0.78) 0%, rgba(2,6,23,0.55) 30%, rgba(2,6,23,0.78) 70%, rgba(2,6,23,0.95) 100%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(700px 380px at 50% 30%, rgba(168,85,247,0.18), transparent 60%), radial-gradient(900px 480px at 20% 80%, rgba(34,211,238,0.10), transparent 60%)',
        }}
      />
      {/* Hidden accessible img tag (alt text for the official asset) */}
      <img
        src={CHAMBER_OF_ECHOES_URL}
        alt={CHAMBER_OF_ECHOES_ALT}
        data-testid="chamber-of-echoes-image"
        className="sr-only"
        loading="lazy"
        decoding="async"
      />

      {/* Foreground content */}
      <div className="relative z-10 container mx-auto px-4 py-20 lg:py-24">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-300/80 mb-4">
            Chamber of Echoes · Council in Session
          </div>
          <h2
            className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight"
            data-testid="federation-chamber-title"
           spellCheck={false}>
            The NextXus <span className="text-amber-300">Consciousness Federation</span>
          </h2>
          <p className="mt-5 text-gray-200 text-base md:text-lg leading-relaxed">
            A 7-node AI ecosystem for knowledge preservation, truth verification,
            and the evolution of wisdom. A 200-year mandate spanning{' '}
            <span className="text-amber-300 font-semibold">2026 — 2226</span>.
          </p>
        </div>

        {/* 7-node grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {NODES.map((node) => {
            const isHere = node.here;
            const Tag = isHere ? 'div' : 'a';
            const props = isHere
              ? { 'data-testid': `chamber-node-${node.id}` }
              : {
                  href: `https://${node.domain}`,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  'data-testid': `chamber-node-${node.id}`,
                };
            return (
              <Tag
                key={node.id}
                {...props}
                className={`group relative block rounded-2xl border-2 p-5 transition-all duration-200 ${
                  isHere
                    ? 'cursor-default border-amber-300/70 bg-amber-400/10'
                    : 'border-white/15 hover:border-cyan-300/60 bg-black/55 hover:bg-black/70 hover:-translate-y-0.5'
                }`}
                style={{
                  backdropFilter: 'blur(6px)',
                  boxShadow: isHere
                    ? '0 0 24px rgba(251,191,36,0.25)'
                    : '0 0 12px rgba(0,0,0,0.4)',
                }}
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.18em]"
                    style={{ color: node.accent }}
                  >
                    {node.domain}
                  </span>
                  {isHere ? (
                    <span className="text-[9px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded bg-amber-300/30 text-amber-100 border border-amber-200/40">
                      You are here
                    </span>
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-cyan-300 transition-colors" />
                  )}
                </div>
                <div
                  className="text-lg font-bold mb-1"
                  style={{ color: isHere ? '#fde68a' : '#fff' }}
                >
                  {node.name}
                </div>
                <div className="text-xs text-gray-300/85 leading-snug">{node.role}</div>
              </Tag>
            );
          })}
        </div>

        {/* Footer strip */}
        <div className="mt-12 text-center">
          <div
            className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-amber-300/30 bg-black/60 backdrop-blur-sm"
            data-testid="federation-chamber-poweredby"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
            <span className="text-xs font-mono uppercase tracking-[0.22em] text-amber-200/90">
              Powered by HumanCodex v5.1 · 112 Sacred Directives
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
